import React from 'react';
import { FormControlLabel, Skeleton, Stack, Switch, Tooltip, Typography } from '@mui/material';
import { useLocales, useTheme } from '../../../../../hooks';
import { DataPresetResponse } from '../../../../../API';
import { HelpText } from '../../../HelpText/HelpText';
import { makeStyles } from 'tss-react/mui';
import IconButton from '../../../IconButton';
import { Delete, Edit } from '@mui/icons-material';
import { HeroThumbnail } from '../../../../Layouts/UIModuleListItem/ContentPreview/Thumbnails/HeroThumbnail';
import { ThumbnailOrientation } from '../../../../../utils/types/genericTypes';
import { NewHeroPresetTypes } from '../../../../../utils/uiModuleUtils';
import { useData } from '../../../../../data-layer';
import { useRecoilValue } from 'recoil';
import { useStyles as useHeroPresetStyles } from '../HeroPresets';
import { heroPresetItemTestIds } from '../../../TestsIds';

export interface HeroPresetItemProps {
  presetId: string;
  onRemove: () => unknown;
  onMultipleArtworkChange: (preset: DataPresetResponse, multipleArtwork: boolean) => unknown;
  multipleArtworkDisabled?: boolean;
  'data-testid'?: string;
}

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: theme.spacing(4),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  thumbnail: {
    height: 160
  }
}));

export function HeroPresetItem({
  presetId,
  onRemove,
  onMultipleArtworkChange,
  multipleArtworkDisabled = false,
  'data-testid': dataTestId
}: HeroPresetItemProps): JSX.Element {
  const { t } = useLocales();
  const { formControlColor } = useTheme();
  const { classes } = useStyles();
  const { classes: hpClasses } = useHeroPresetStyles();
  const {
    presets: {
      state: { withFormMetadata, withRecordById },
      hook: { edit }
    }
  } = useData();

  const preset = useRecoilValue(withRecordById(presetId));
  const formMetadata = useRecoilValue(withFormMetadata);

  const onEdit = () => edit(presetId);

  if (!preset) {
    return <Skeleton className={hpClasses.skeleton} animation="wave" />;
  }

  return (
    <Stack direction="row" alignItems="center" className={classes.root} gap={2} data-testid={dataTestId}>
      <Typography variant="body1" flexGrow={1}>
        {preset.name}
      </Typography>
      <Stack direction="row" gap={2}>
        <div className={classes.thumbnail}>
          <HeroThumbnail
            heroContentItem={preset.data as NewHeroPresetTypes}
            orientation={ThumbnailOrientation.LANDSCAPE}
          />
        </div>
        <div className={classes.thumbnail}>
          <HeroThumbnail
            heroContentItem={preset.data as NewHeroPresetTypes}
            orientation={ThumbnailOrientation.PORTRAIT}
          />
        </div>
      </Stack>
      <FormControlLabel
        control={
          <Switch
            size="medium"
            color={formControlColor}
            checked={!!(preset.data as NewHeroPresetTypes)?.multipleArtwork}
            disabled={multipleArtworkDisabled}
            onChange={({ target }) => onMultipleArtworkChange(preset, target.checked)}
            inputProps={
              {
                'data-testid': heroPresetItemTestIds.multipleArtworkSwitch
              } as React.InputHTMLAttributes<HTMLInputElement>
            }
          />
        }
        label={
          <Tooltip title={<HelpText>{t('layouts.multiple_artwork_helper')}</HelpText>} placement="top-start">
            <span>{t('layouts.multiple_artwork')}</span>
          </Tooltip>
        }
      />
      <div>
        <IconButton
          disabled={formMetadata.isShowingForm}
          onClick={onEdit}
          title={t('presets.edit_preset')}
          size="large"
          data-testid={heroPresetItemTestIds.edit}
        >
          <Edit />
        </IconButton>
        <IconButton
          onClick={onRemove}
          title={t('presets.delete_preset')}
          size="large"
          data-testid={heroPresetItemTestIds.delete}
        >
          <Delete />
        </IconButton>
      </div>
    </Stack>
  );
}
